import { IN_APP } from 'constants/messageMediums';
import {
  HIGH_LEVEL_LABELS_TO_VALUES,
  METRIC_LABELS_TO_VALUES,
} from 'constants/metricLabelsToValues';
import { ASCENDING } from 'constants/sortDirections';
import {
  selectIsContextualizedMetricsFeatureFlagOn,
  selectIsMIApplyButtonFeatureFlagOn,
  selectIsMetricDefinitionsFeatureFlagOn,
  selectIsWebInAppReportingFeatureFlagOn,
} from 'core/userContext';
import { CampaignRetargetIconWithTooltip } from 'modules/ai/nba/components/campaigns/CampaignRetargetIconWithTooltip';
import { DefinitionWrapper } from 'modules/metricDefinitions';
import { AppIcon } from 'modules/mobileApps/components/AppIcon';
import { Cell, CenterText, HiddenCSVLink, RightAlignText } from 'modules/productAnalytics/Styled';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RUM_PURPOSE, useRumAction } from 'services/observability/datadog';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { ARROW_DIRECTION_DOWN, CollapsibleContentArrow } from 'ui/collapsibleContent';
import { ChevronDown16, Export16 } from 'ui/icons';
import { NoResult } from 'ui/illustrations';
import { Menu } from 'ui/menu';
import { PageSize } from 'ui/pageSize/PageSize';
import { Pagination } from 'ui/pagination';
import { DraggableTable } from 'ui/table/DraggableTable';
import { sortColumns, sortRows } from 'ui/table/utils';
import DraggableHeader from 'ui/tableHeader/DraggableHeader';
import { colors } from 'ui/theme';
import { getNewDate } from 'utils/date';
import { useAllowPreviewBaus } from 'utils/hooks/useAllowPreviewBaus';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import {
  exportCampaignsAsCsvRequest,
  exportDestinationsAsCsvRequest,
  fetchAllMetricsForCampaignsRequest,
  fetchSortedCampaignIdsByMultiMediumMetricRequest,
  getPreviewThumbnailsRequest,
} from '../actions';
import {
  selectAllMetricsForCampaignsLoading,
  selectAllMetricsPerCampaign,
  selectAppliedCrossMetrics,
  selectCampaignIdsToFetchMetrics,
  selectCampaignMetricsTableData,
  selectCampaignsLoading,
  selectError,
  selectProjectId,
  selectSortedCampaignIdsByMetric,
  selectSortedCampaignIdsByMetricLoading,
  selectTemplateIds,
} from '../selectors';
import { TruncatedHeaderCell } from './TruncatedHeaderCell';

import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import {
  ArrowButton,
  CampaignCaption,
  CampaignCaptionItem,
  CampaignsHeader,
  CenterCol,
  CenterTextSpaceBetween,
  ChildrenCell,
  Container,
  ControlRow,
  FlexAlignCenter,
  PaddingRight,
  PreviewContainer,
  SideCol,
  StyledImgSkeleton,
  StyledTableHeaderCell,
  StyledTableRows,
  TableWrapper,
} from '../Styled';
import { InAppMetricsToBreakdown, MediumOptions, FETCH_ERRORS as e } from '../constants';
import {
  useCampaignAndMetricFiltersContext,
  useTabSpecificFiltersContext,
  useTimeFiltersContext,
} from '../context';
import { Campaign, ColumnData } from '../types';
import {
  addExpandStateToCampaignMetricData,
  getCampaignMediumsByFrequency,
  getCampaignTotalText,
  getLoadingRows,
  getMetricColumns,
  getPartialLoadingRows,
  paginate,
  parseCampaignMetricData,
  parseLabelsFromSelectedMetrics,
  stringifyVals,
} from '../utils';
import { NoDataMessage } from './NoDataMessage';

type CampaignsTableProps = {
  previewThumbnails: Record<string, string>;
  nbaEnabledCampaignIds: number[];
};

export const CampaignsTable = ({
  previewThumbnails,
  nbaEnabledCampaignIds,
}: CampaignsTableProps) => {
  const dispatch = useDispatch();
  const isAnalyticsBotFiltering = useFeatureFlag('AnalyticsBotFiltering');
  const addSignal = useRumAction(RUM_PURPOSE.TableColumn);
  const {
    selectedCrossMetrics,
    selectedMediums,
    mediumMetrics,
    overallMetrics,
    MediumFilteredHLMs,
    previousCampaignTableFilters,
    setPreviousCampaignTableFilters,
  } = useCampaignAndMetricFiltersContext();
  const { timezone, dateParams } = useTimeFiltersContext();
  const {
    campaignsSortedBy: sortedBy,
    setCampaignsSortedBy: setSortedBy,
    campaignsSortDirection: sortDirection,
    setCampaignsSortDirection: setSortDirection,
  } = useTabSpecificFiltersContext();
  // List of high level metrics fetched from graphql
  const { highLevelMetricsValueTypes, analyticsMetricsValueTypes } = useMetricsContext();

  const [pageVals, setPageVals] = useState({
    pageSize: 10,
    currentPage: 1,
  });
  const { pageSize, currentPage } = pageVals;
  const projectId = useSelector(selectProjectId);
  const [expandState, setExpandState] = useState<Record<number, boolean>>({});

  const campaignIdsToFetchMetrics = useSelector(selectCampaignIdsToFetchMetrics);
  const campaignMetricsTableData: Campaign[] = useSelector(selectCampaignMetricsTableData);
  const metricsPerCampaign = useSelector(selectAllMetricsPerCampaign);
  const inAppFFOn = useSelector(selectIsWebInAppReportingFeatureFlagOn);
  const isMIApplyButtonFFOn = useSelector(selectIsMIApplyButtonFeatureFlagOn);
  const appliedCrossMetrics: string[] = useSelector(selectAppliedCrossMetrics);

  const [campaignDataAsCsv, setCampaignDataAsCsv] = useState('');
  const [destinationDataAsCsv, setDestinationDataAsCsv] = useState('');

  const allowPreview = useAllowPreviewBaus('WebInApp');
  const showInAppDestinations = inAppFFOn || allowPreview;

  const sortedCampaignIdsByMetric = useSelector(selectSortedCampaignIdsByMetric);
  const templateIds = useSelector(selectTemplateIds);

  // Loading selectors
  const metricsForCampaignsLoading = useSelector(selectAllMetricsForCampaignsLoading);
  const sortedCampaignIdsByMetricLoading = useSelector(selectSortedCampaignIdsByMetricLoading);
  const campaignsLoading = useSelector(selectCampaignsLoading);

  const isCampaignsTableDataLoaded = !(
    metricsForCampaignsLoading ||
    campaignsLoading ||
    sortedCampaignIdsByMetricLoading
  );

  // Campaigns overview (breakdown by medium)
  const totalCampaigns = campaignMetricsTableData.length;
  const numCampaignsPerMedium = getCampaignMediumsByFrequency(campaignMetricsTableData);
  const headerText = getCampaignTotalText(numCampaignsPerMedium, totalCampaigns);

  const BASE_HEADER_COLUMNS: ColumnData[] = [
    {
      name: 'Campaign name',
      value: 'campaignName',
      align: 'flex-start',
      RowCell: ({ row }: any) => {
        return (
          <Cell>
            <CenterTextSpaceBetween>
              <CenterText>
                {previewThumbnails && previewThumbnails[row.templateId] ? (
                  <PreviewContainer src={previewThumbnails[row.templateId]} />
                ) : (
                  <StyledImgSkeleton />
                )}
                <span>
                  <TruncatedHeaderCell name={row.campaignName} id={row.campaignId} maxWidth={256} />
                  <CampaignCaption>
                    <CampaignCaptionItem>ID: {row.campaignId}</CampaignCaptionItem>
                    <CampaignCaptionItem>{row.campaignType}</CampaignCaptionItem>
                    <CampaignCaptionItem>{row.campaignState}</CampaignCaptionItem>
                  </CampaignCaption>
                </span>
              </CenterText>
              {nbaEnabledCampaignIds.includes(row.campaignId) && (
                <CampaignRetargetIconWithTooltip id={row.campaignId} />
              )}
              {showInAppDestinations && row.medium === 'InApp' && row.childrenRows?.length > 0 && (
                <ArrowButton
                  onClick={() =>
                    setExpandState({
                      ...expandState,
                      [row.campaignId]: !expandState[row.campaignId],
                    })
                  }
                  data-test="collapsible-arrow-button"
                  size={BUTTON_SIZES.SMALL}
                >
                  <CollapsibleContentArrow
                    isCollapsed={!expandState[row.campaignId]}
                    arrowStart={ARROW_DIRECTION_DOWN}
                    arrowEnd={180}
                    width={16}
                    height={16}
                  />
                </ArrowButton>
              )}
            </CenterTextSpaceBetween>
          </Cell>
        );
      },
      childrenValue: 'appName',
      ChildrenCell: ({ row }: any) => (
        <ChildrenCell>
          <RightAlignText>
            <PaddingRight>{row.appName}</PaddingRight>
          </RightAlignText>
        </ChildrenCell>
      ),
    },
    {
      name: 'Medium',
      value: 'medium',
      align: 'flex-start',
      RowCell: ({ row }: any) => (
        <Cell>
          <CenterText>{MediumOptions[row.medium]}</CenterText>
        </Cell>
      ),
      ChildrenCell: ({ row }: any) => (
        <ChildrenCell>
          <FlexAlignCenter>
            <AppIcon platformType={row?.platform} width={16} color={colors.secondaryText} />
            {row?.platform}
          </FlexAlignCenter>
        </ChildrenCell>
      ),
    },
    {
      name: 'Launch date',
      value: 'launchDate',
      align: 'flex-start',
      RowCell: ({ row }: any) => (
        <Cell>
          {row.launchDate
            ? getNewDate({ date: row.launchDate, timeZone: timezone }).format('M/DD/YY, h:mm A')
            : '-'}
        </Cell>
      ),
      ChildrenCell: () => <ChildrenCell></ChildrenCell>,
    },
  ];

  const BASE_HEADER_COLUMN_VALUES = ['campaignName', 'launchDate', 'medium'];

  const sortByMetric = ![...BASE_HEADER_COLUMN_VALUES, 'id'].includes(sortedBy);
  const { startTime: start, endTime: end } = dateParams;
  const startTime = start?.toISOString() || '';
  const endTime = end?.toISOString() || '';

  useEffect(() => {
    if (sortByMetric) {
      const sortByMetricParams = {
        startTime,
        endTime,
        campaignIds: campaignIdsToFetchMetrics,
        ascending: sortDirection === ASCENDING,
      };

      // @ts-ignore
      if (MediumFilteredHLMs.has(HIGH_LEVEL_LABELS_TO_VALUES[sortedBy])) {
        // @ts-ignore
        sortByMetricParams.overallMetric = { enumName: HIGH_LEVEL_LABELS_TO_VALUES[sortedBy] };
      } else {
        // @ts-ignore
        sortByMetricParams.mediumMetric = {
          // @ts-ignore
          name: METRIC_LABELS_TO_VALUES(isAnalyticsBotFiltering)[sortedBy],
        };
      }
      dispatch(fetchSortedCampaignIdsByMultiMediumMetricRequest(sortByMetricParams));
    }
  }, [sortedBy, sortDirection, campaignIdsToFetchMetrics]);

  const parsedCampaignMetricData = parseCampaignMetricData(
    campaignMetricsTableData,
    showInAppDestinations,
  );
  const parsedCampaignMetricDataWithExpandState = addExpandStateToCampaignMetricData(
    parsedCampaignMetricData,
    expandState,
  );

  const metricSet = new Set(
    parseLabelsFromSelectedMetrics(
      isMIApplyButtonFFOn ? appliedCrossMetrics : selectedCrossMetrics,
    ),
  );
  const displayMetrics = Array.from(metricSet);
  const sortedRows = sortByMetric
    ? parsedCampaignMetricDataWithExpandState
    : // @ts-expect-error: sortRows expects the actual string keys that are the values of the columns (type keyof DataType)
      // but sortedBy here is typed as a generic "string" type. This will take some time to debug, so for now, we're just
      // going to expect this error to exist.
      sortRows(parsedCampaignMetricDataWithExpandState, sortedBy, sortDirection);
  const paginatedSortedRows = paginate(sortedRows, pageSize, currentPage, setPageVals);
  const paginatedIds = paginatedSortedRows.map((el) => el.campaignId);
  const total = sortedRows.length;
  const ValueTypes = { ...highLevelMetricsValueTypes, ...analyticsMetricsValueTypes };
  const consolidatedColumns = [
    ...BASE_HEADER_COLUMNS,
    ...getMetricColumns(displayMetrics, dateParams, ValueTypes),
  ];

  const [messagingInsightsLocalOrder, setmessagingInsightsLocalOrder] = useLocalStorage<string[]>(
    `proj-${projectId}-messagingInsightsColOrder`,
    [],
  );

  const [columnOrder, setColumnOrder] = useState(
    messagingInsightsLocalOrder?.length > 0
      ? messagingInsightsLocalOrder
      : consolidatedColumns.map((el: ColumnData) => el.value),
  );

  const sortedColumns = sortColumns(columnOrder, consolidatedColumns).filter((x) => !!x);
  const manageLoadingRows = () => {
    if (metricsForCampaignsLoading || sortedCampaignIdsByMetricLoading) {
      return getPartialLoadingRows(paginatedSortedRows, displayMetrics);
    }
    return getLoadingRows(
      ['campaignType', 'campaignState', 'campaignName', ...displayMetrics],
      pageSize,
    );
  };

  const inAppBreakdownMetrics: Record<string, string>[] = (
    isMIApplyButtonFFOn ? appliedCrossMetrics : selectedCrossMetrics
  ).reduce((acc: Record<string, string>[], curr) => {
    if (inAppFFOn && InAppMetricsToBreakdown[curr]) {
      acc.push({ name: InAppMetricsToBreakdown[curr] });
    }
    return acc;
  }, []);

  // Fetch metric data
  const metrics = { mediumMetrics: [...mediumMetrics, ...inAppBreakdownMetrics], overallMetrics };
  const queryData = {
    startTime,
    endTime,
    campaignIds: paginatedIds,
  };

  const sortMetricsByName = (metArr: any[]) => metArr.map((met) => met.enumName || met.name).sort();
  const valueStr = stringifyVals([
    startTime,
    endTime,
    ...paginatedIds.sort(),
    ...sortMetricsByName(metrics.mediumMetrics),
    ...sortMetricsByName(metrics.overallMetrics),
  ]);
  const fetchMetricsForCampaigns = () => {
    if (valueStr !== previousCampaignTableFilters) {
      setPreviousCampaignTableFilters(valueStr);
      dispatch(fetchAllMetricsForCampaignsRequest({ ...queryData, metrics }));
    }
  };

  useEffect(() => {
    if (
      campaignIdsToFetchMetrics?.length > 0 &&
      paginatedSortedRows?.length &&
      !isMIApplyButtonFFOn
    ) {
      fetchMetricsForCampaigns();
    }
  }, [campaignIdsToFetchMetrics, selectedCrossMetrics, pageVals, startTime, endTime]);

  useEffect(() => {
    if (
      campaignIdsToFetchMetrics?.length > 0 &&
      paginatedSortedRows?.length &&
      isMIApplyButtonFFOn
    ) {
      fetchMetricsForCampaigns();
    }
  }, [campaignIdsToFetchMetrics, appliedCrossMetrics, pageVals, startTime, endTime]);

  // Fetch metric data (when sorting by a metric)
  useEffect(() => {
    if (
      sortedCampaignIdsByMetric?.length &&
      paginatedIds.filter((id) => !metricsPerCampaign?.[id]).length
    ) {
      fetchMetricsForCampaigns();
    }
  }, [sortedCampaignIdsByMetric]);

  // Fetch metric data (when sorting by a non-metric column such as Name, Medium)
  useEffect(() => {
    if (!sortByMetric) {
      if (paginatedIds.filter((id) => !metricsPerCampaign?.[id]).length) {
        fetchMetricsForCampaigns();
      }
    }
  }, [sortDirection, sortedBy]);

  // Fetch thumbnails
  useEffect(() => {
    if (valueStr !== previousCampaignTableFilters && templateIds.length > 0) {
      if (paginatedSortedRows) {
        const paginatedTemplateIds = paginatedSortedRows.map((el) => el.templateId);
        dispatch(getPreviewThumbnailsRequest({ templateIds: paginatedTemplateIds }));
      } else {
        dispatch(getPreviewThumbnailsRequest({ templateIds }));
      }
    }
  }, [templateIds]);

  // Error state
  const errors = useSelector(selectError) || {};
  const campaignFetchErrors = new Set([
    e.FETCH_CAMPAIGNS,
    e.FETCH_ALL_METRICS_FOR_CAMPAIGNS,
    e.FETCH_SORTED_CAMPAIGN_IDS,
  ]);
  const showErrorState = Object.keys(errors).some((err) => campaignFetchErrors.has(err));

  const exportCsvQueryParams = {
    startTime,
    endTime,
    metrics: { mediumMetrics, overallMetrics },
    campaignIds: campaignIdsToFetchMetrics,
  };

  const exportDestinationQueryParams = {
    startTime,
    endTime,
    metrics: { mediumMetrics: inAppBreakdownMetrics },
    campaignIds: campaignIdsToFetchMetrics,
  };

  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const destinationLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(
    null,
  );

  const isCsvExportDisabled =
    (isMIApplyButtonFFOn ? appliedCrossMetrics : selectedCrossMetrics)?.length === 0 ||
    exportCsvQueryParams?.campaignIds?.length === 0;

  // Callback to mimic the button click for CSVLink
  // This callback is called after the csv data is fetched, so that the data prop in the CSVLink is updated
  const clickExportCsvButtonCallback = (csvData: string) => {
    flushSync(() => {
      setCampaignDataAsCsv(csvData);
    });

    csvLinkRef?.current?.link.click();
  };

  const clickExportDestinationButtonCallback = (csvData: string) => {
    flushSync(() => {
      setDestinationDataAsCsv(csvData);
    });
    // Callback to mimic the button click for CSVLink
    // This callback is called after the csv data is fetched, so that the data prop in the CSVLink is updated
    destinationLinkRef?.current?.link.click();
  };

  const handleExportCsv = () => {
    dispatch(exportCampaignsAsCsvRequest(exportCsvQueryParams, clickExportCsvButtonCallback));
  };

  const handleExportDestination = () => {
    dispatch(
      exportDestinationsAsCsvRequest(
        exportDestinationQueryParams,
        clickExportDestinationButtonCallback,
      ),
    );
  };

  const overflowActions: Record<string, any> = {
    'export-campaign-table': handleExportCsv,
    'export-inapp-events': handleExportDestination,
  };

  // Add metric definition tooltips to column headers
  const metricDefFFOn = useSelector(selectIsMetricDefinitionsFeatureFlagOn);
  const contextualizedMetricsFFOn = useSelector(selectIsContextualizedMetricsFeatureFlagOn);
  const metricDefColumns = sortedColumns?.map((col) => ({
    ...col,
    name: <DefinitionWrapper>{col?.name as string}</DefinitionWrapper>,
  }));

  return (
    <Container>
      {!showErrorState && (
        <>
          <CampaignsHeader>
            <div>{headerText}</div>
            <>
              {showInAppDestinations && selectedMediums.includes(IN_APP) ? (
                <Menu
                  handleSelectionChange={(id: string) => id && overflowActions[id]()}
                  options={[
                    { label: 'Export campaign table', id: 'export-campaign-table' },
                    { label: 'Export in-app campaigns by destination', id: 'export-inapp-events' },
                  ]}
                >
                  <SecondaryButton>
                    <Export16 /> Export metrics <ChevronDown16 />
                  </SecondaryButton>
                </Menu>
              ) : (
                <SecondaryButton isDisabled={isCsvExportDisabled} onClick={handleExportCsv}>
                  <Export16 /> Export campaign table
                </SecondaryButton>
              )}
              <HiddenCSVLink
                data={campaignDataAsCsv}
                filename={'campaign_table.csv'}
                ref={csvLinkRef}
                target="_blank"
              />
              <HiddenCSVLink
                data={destinationDataAsCsv}
                filename={'destination_table.csv'}
                ref={destinationLinkRef}
                target="_blank"
              />
            </>
          </CampaignsHeader>
          <TableWrapper>
            {/* @ts-ignore */}
            <DraggableTable
              columnOrder={columnOrder}
              setColumnOrder={(order) => {
                setColumnOrder(order);
                setmessagingInsightsLocalOrder(order);
                addSignal('CAMPAIGN_TABLE_COLUMN_ORDER', { order });
              }}
            >
              <DraggableHeader
                columns={
                  metricDefFFOn && !contextualizedMetricsFFOn ? metricDefColumns : sortedColumns
                }
                sortedBy={sortedBy}
                setSortedBy={setSortedBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                isSticky
                isStaticFirstCol
                HeaderCell={StyledTableHeaderCell}
              />
              <StyledTableRows
                idKey="campaignId"
                rows={isCampaignsTableDataLoaded ? paginatedSortedRows : manageLoadingRows()}
                columns={sortedColumns}
                isCollapsible={true}
              />
            </DraggableTable>
          </TableWrapper>
          <ControlRow>
            <SideCol />
            <CenterCol>
              <Pagination
                pages={Math.ceil(total / pageSize)}
                onChangePage={(page) => {
                  setPageVals({ pageSize, currentPage: page });
                }}
                page={currentPage}
              />
            </CenterCol>
            <SideCol>
              <PageSize
                total={total}
                onSelect={(size) => {
                  setPageVals({ pageSize: size, currentPage: 1 });
                }}
                pageSizes={[10, 20, 30, 40, 50]}
              />
            </SideCol>
          </ControlRow>
        </>
      )}
      {showErrorState && (
        <NoDataMessage
          Icon={NoResult}
          title="Sorry we couldn't load this page"
          subtitle="Try refreshing the page. If this keeps happening, let us know."
        />
      )}
    </Container>
  );
};
